import type { User } from '../../types';
import type { AuthLogger } from '../../shared/auth-logger';
import type { HttpClient } from '../client-app/index';
import { HttpResponseError } from '../http';
import { TokenExchangeStep } from '../../constants';

interface TokenExchangeResult {
  user?: User;
  needsPhone: boolean;
  idToken: string;
}

type TokenExchangeAttributes = {
  'first-name': string;
  'last-name': string;
  'home-phone': string;
  email: string;
  jwt: string;
  newsletter: boolean;
  'sso-provider': string | null;
  'has-password': boolean;
  'hide-imageless-listings'?: boolean;
  locale: string | null;
};

type TokenExchangeSuccessBody = {
  data: { id: string | number; attributes: TokenExchangeAttributes };
};

type TokenExchangeRequestBody = {
  phone?: string;
  'first-name'?: string;
  'last-name'?: string;
};

function parseUser(response: unknown): User {
  const body = response as TokenExchangeSuccessBody;
  const attrs = body?.data?.attributes;
  if (!attrs) {
    throw new Error('Unable to exchange token: invalid token exchange response.');
  }
  return {
    id: body.data.id,
    firstName: attrs['first-name'],
    lastName: attrs['last-name'],
    homePhone: attrs['home-phone'],
    email: attrs.email,
    jwt: attrs.jwt,
    newsletter: attrs.newsletter,
    ssoProvider: attrs['sso-provider'],
    hasPassword: attrs['has-password'],
    hideImagelessListings: attrs['hide-imageless-listings'] ?? false,
    locale: attrs.locale ?? '',
  };
}

export interface TokenExchangeRegistrationFields {
  phone?: string;
  profile?: {
    firstName: string;
    lastName: string;
  };
}

export async function tokenExchange(
  http: HttpClient,
  idToken: string,
  fields?: TokenExchangeRegistrationFields,
  logger?: AuthLogger
): Promise<TokenExchangeResult> {
  const requestBody: TokenExchangeRequestBody = {};
  if (fields?.phone) requestBody.phone = fields.phone;
  if (fields?.profile) {
    requestBody['first-name'] = fields.profile.firstName;
    requestBody['last-name'] = fields.profile.lastName;
  }
  const hasFields = Boolean(fields && (fields.phone || fields.profile));

  try {
    logger?.log(TokenExchangeStep.Request, { hasFields });
    const response = await http.request<TokenExchangeSuccessBody>(
      '/services/api/v4/auth/token_exchange',
      'POST',
      hasFields ? requestBody : undefined,
      { Authorization: `Bearer ${idToken}` }
    );
    const user = parseUser(response);
    logger?.log(TokenExchangeStep.Success, { userId: user.id, email: user.email, ssoProvider: user.ssoProvider });
    return { user, needsPhone: false, idToken };
  } catch (error) {
    if (error instanceof HttpResponseError && error.code === 'registration_required') {
      logger?.log(TokenExchangeStep.RegistrationRequired);
      return { needsPhone: true, idToken };
    }
    const message = error instanceof Error ? error.message : String(error);
    logger?.log(TokenExchangeStep.Error, { error: message });
    throw error;
  }
}

export async function tokenExchangeWithPhone(
  http: HttpClient,
  idToken: string,
  phone: string,
  profile?: { firstName: string; lastName: string } | null,
  logger?: AuthLogger
): Promise<User> {
  logger?.log(TokenExchangeStep.SubmitPhoneRequest, { phone: '***' });
  const body: TokenExchangeRequestBody = { phone };
  if (profile) {
    body['first-name'] = profile.firstName;
    body['last-name'] = profile.lastName;
  }
  const response = await http.request<TokenExchangeSuccessBody>(
    '/services/api/v4/auth/token_exchange',
    'POST',
    body,
    { Authorization: `Bearer ${idToken}` }
  );
  const user = parseUser(response);
  logger?.log(TokenExchangeStep.SubmitPhoneSuccess, { userId: user.id, email: user.email });
  return user;
}
